import React, { useState, useEffect } from "react";
import PWAInstallPrompt from "@/components/ui/PWAInstallPrompt";
import { useTranslation } from "react-i18next";

export default function PWAInstallPage() {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState<"ios" | "android" | "other">("other");

  useEffect(() => {
    // Xác định platform
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isIOS) {
      setPlatform("ios");
    } else if (isAndroid) {
      setPlatform("android");
    }
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">
        {t("pwa.installTitle", "Cài đặt ứng dụng Giác Ngộ")}
      </h1>
      
      <p className="mb-6">
        {t("pwa.installDescription", "Cài đặt ứng dụng Giác Ngộ trên thiết bị của bạn để truy cập nhanh chóng và dễ dàng hơn. Không cần tải app từ cửa hàng, chỉ cần làm theo hướng dẫn bên dưới.")}
      </p>
      
      <div className="bg-muted/20 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-2">
          {t("pwa.whyInstall", "Tại sao nên cài đặt?")}
        </h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>{t("pwa.benefit1", "Truy cập nhanh từ màn hình chính")}</li>
          <li>{t("pwa.benefit2", "Trải nghiệm giống ứng dụng native")}</li>
          <li>{t("pwa.benefit3", "Không chiếm nhiều dung lượng như app thông thường")}</li>
          <li>{t("pwa.benefit4", "Tự động cập nhật phiên bản mới nhất")}</li>
          <li>{t("pwa.benefit5", "Không cần tải từ App Store hoặc Google Play")}</li>
        </ul>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t("pwa.installGuide", "Hướng dẫn cài đặt")}</h2>
        
        {platform === "ios" && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium">{t("pwa.ios.title", "Cài đặt trên iPhone/iPad")}</h3>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/ios-step1.jpg" alt="iOS Step 1" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.ios.step1Title", "Bước 1: Nhấn nút Chia sẻ")}</h4>
                <p>{t("pwa.ios.step1Desc", "Nhấn vào biểu tượng Chia sẻ (hình hộp với mũi tên hướng lên) ở dưới cùng (iPhone) hoặc trên cùng (iPad) của trình duyệt Safari.")}</p>
              </div>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/ios-step2.jpg" alt="iOS Step 2" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.ios.step2Title", "Bước 2: Cuộn và chọn \"Thêm vào màn hình chính\"")}</h4>
                <p>{t("pwa.ios.step2Desc", "Cuộn xuống trong menu và nhấn vào \"Thêm vào màn hình chính\" (Add to Home Screen).")}</p>
              </div>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/ios-step3.jpg" alt="iOS Step 3" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.ios.step3Title", "Bước 3: Nhấn \"Thêm\"")}</h4>
                <p>{t("pwa.ios.step3Desc", "Nhấn nút \"Thêm\" ở góc trên bên phải để xác nhận thêm ứng dụng vào màn hình chính của bạn.")}</p>
              </div>
            </div>
          </div>
        )}
        
        {platform === "android" && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium">{t("pwa.android.title", "Cài đặt trên Android")}</h3>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/android-step1.jpg" alt="Android Step 1" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.android.step1Title", "Bước 1: Nhấn vào biểu tượng Menu")}</h4>
                <p>{t("pwa.android.step1Desc", "Nhấn vào biểu tượng 3 chấm (⋮) ở góc trên bên phải của trình duyệt Chrome.")}</p>
              </div>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/android-step2.jpg" alt="Android Step 2" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.android.step2Title", "Bước 2: Chọn \"Thêm vào màn hình chính\"")}</h4>
                <p>{t("pwa.android.step2Desc", "Nhấn vào tùy chọn \"Thêm vào màn hình chính\" (Add to Home screen) trong menu.")}</p>
              </div>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <img src="/images/pwa/android-step3.jpg" alt="Android Step 3" className="w-40 h-auto object-contain mx-auto" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-base">{t("pwa.android.step3Title", "Bước 3: Nhấn \"Thêm\"")}</h4>
                <p>{t("pwa.android.step3Desc", "Xác nhận bằng cách nhấn \"Thêm\" (Add) trong hộp thoại hiện ra.")}</p>
              </div>
            </div>
          </div>
        )}
        
        {platform === "other" && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p>{t("pwa.otherDevice", "Bạn đang sử dụng thiết bị máy tính. Để cài đặt ứng dụng, vui lòng truy cập trang web này từ điện thoại hoặc máy tính bảng của bạn.")}</p>
          </div>
        )}
      </div>
      
      {/* Hướng dẫn sử dụng PWA sau khi cài đặt */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t("pwa.afterInstall", "Sau khi cài đặt")}</h2>
        <p className="mb-4">{t("pwa.afterInstallDesc", "Sau khi cài đặt, bạn sẽ thấy biểu tượng ứng dụng Giác Ngộ trên màn hình chính của thiết bị. Nhấn vào biểu tượng để mở ứng dụng như bình thường.")}</p>
        
        <h3 className="text-lg font-medium mt-4 mb-2">{t("pwa.features", "Các tính năng khác biệt")}</h3>
        <ul className="list-disc pl-5 space-y-2">
          <li>{t("pwa.feature1", "Giao diện toàn màn hình không có thanh địa chỉ của trình duyệt")}</li>
          <li>{t("pwa.feature2", "Hoạt động nhanh hơn vì dữ liệu được lưu trữ cục bộ")}</li>
          <li>{t("pwa.feature3", "Có thể sử dụng offline với một số tính năng")}</li>
        </ul>
      </div>
      
      {/* Hiển thị popup hướng dẫn */}
      <div className="mt-4 mb-10">
        <h3 className="text-lg font-medium mb-2">{t("pwa.popup", "Hướng dẫn trực quan:")}</h3>
        <PWAInstallPrompt forceShow={true} />
      </div>
    </div>
  );
} 